import { devtools } from 'zustand/middleware'
import { shallow } from 'zustand/shallow'
import { createWithEqualityFn } from 'zustand/traditional'

export const APP_LOADER_STORE_KEY = '@bitlyft/app-loader'

type AppLoaderState = {
	isLoading: boolean
	showProgress: boolean
}

type AppLoaderActions = {
	showLoader: () => void
	hideLoader: () => void
	setShowProgress: (value: boolean) => void
}

type AppLoaderStore = AppLoaderState & {
	actions: AppLoaderActions
}

const initialState: AppLoaderState = {
	isLoading: false,
	showProgress: false,
}

/**
 * Vanilla Zustand store backing {@link useAppLoader}.
 *
 * Exposed at module scope so non-React callers — most importantly the
 * `_dashboard` route's `beforeLoad` — can drive the AppLoader during tenant
 * switches and other router-orchestrated flows.
 *
 * @see knowledge/reference/tenant-switching.md
 */
const appLoaderStore = createWithEqualityFn<AppLoaderStore>()(
	devtools(
		(set) => ({
			...initialState,
			actions: {
				showLoader: () =>
					set({ isLoading: true }, false, 'appLoader/showLoader'),
				hideLoader: () =>
					set(
						{ isLoading: false, showProgress: false },
						false,
						'appLoader/hideLoader',
					),
				setShowProgress: (value) =>
					set({ showProgress: value }, false, 'appLoader/setShowProgress'),
			},
		}),
		{ name: APP_LOADER_STORE_KEY },
	),
	shallow,
)

export const useAppLoaderIsLoading = () =>
	appLoaderStore((state) => state.isLoading)
export const useAppLoaderShowProgress = () =>
	appLoaderStore((state) => state.showProgress)
export const useAppLoaderActions = () =>
	appLoaderStore((state) => state.actions)

// Vanilla callers (e.g. router beforeLoad) use
// `appLoaderStore.getState().actions.X()` directly.
export { appLoaderStore }
